/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LicensesCollapsedSectionFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";

import {
	BackgroundContainer,
	CosmosText,
	CosmosTextVariantEnum,
	hasContent,
	LicenseTopCard,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { HeadingLevelContext } from "@components/context";
import { cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { LicensesCollapsedCards } from "./LicensesCollapsedCards.organism";

export function LicensesCollapsedSection({
	config,
	topCard,
	collapsedCardsHeading,
	collapsedCards,
	collapsedCta,
	freeHeading,
}: LicensesCollapsedSectionFragment): React.JSX.Element | null {
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);

	const validTopCard =
		!!topCard &&
		(hasContent(topCard.title) || Boolean(topCard.bullets?.length || topCard.checkList || topCard.buttons?.length));
	const validTopCardHeading = hasContent(freeHeading);
	const validContent = validTopCardHeading || validTopCard;

	const validFooter = Boolean(collapsedCardsHeading || collapsedCards?.length || collapsedCta);

	// The footer (its heading h3 + card titles h4) renders on its own guard. When no
	// heading precedes it, shift the whole footer up one level (floored at h2) so it
	// isn't an h1→h3 skip. Gate on actual preceding HEADINGS (not validContent, which
	// is also true for bullets/checklist/buttons that emit no heading).
	const footerHasPrecedingHeading = Boolean(config?.heading?.title) || Boolean(freeHeading) || Boolean(topCard?.title);

	if (!validHeading && !validContent && !validFooter) return null;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)} bgBlur>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack
						gap={{ sm: "eds.dimension.48", smDown: "eds.dimension.40" }}
						alignItems="stretch"
						className={cx("license-cards-collapsed-section")}
					>
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack alignItems="stretch">
								<VStack alignItems="flex-start" gap="eds.dimension.24">
									{validTopCardHeading ? (
										<CosmosText
											variant={CosmosTextVariantEnum.headingLG}
											as={config?.heading?.title ? "h3" : "h2"}
											text={freeHeading || ""}
										/>
									) : null}
									{validTopCard ? (
										<LicenseTopCard
											title={topCard.title}
											// One level below the nearest preceding heading: under freeHeading when it
											// renders (h4 if a section h2 also precedes, else h3), otherwise directly
											// under the section h2 (h3) or the page hero h1 (h2).
											titleAs={
												validTopCardHeading
													? config?.heading?.title
														? "h4"
														: "h3"
													: config?.heading?.title
														? "h3"
														: "h2"
											}
											bullets={topCard.bullets || []}
											checkList={topCard.checkList || []}
											buttons={topCard.buttons || []}
											__typename={topCard.__typename}
										/>
									) : null}
								</VStack>
							</VStack>
						) : null}
						{validFooter ? (
							<HeadingLevelContext.Provider value={{ offset: footerHasPrecedingHeading ? 0 : -1 }}>
								<LicensesCollapsedCards heading={collapsedCardsHeading} cards={collapsedCards} cta={collapsedCta} />
							</HeadingLevelContext.Provider>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
}
